'use client';

import { loadStripe } from '@stripe/stripe-js';
import { IconInfoCircle } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getSession, useSession } from 'next-auth/react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { toast } from 'sonner';

import { getCurrentUserInvoiceInformation } from '@/actions/invoice-information';
import { InvoiceInformationForm } from '@/app/(protected)/(routes)/(external)/platform/_components/upgrade/invoice-information-form';
import { SubscriptionSubscriptionData } from '@/backend-api/Api';
import { LoginForm } from '@/components/login-form';
import { AddPaymentMethodDialog } from '@/components/payment/AddPaymentMethodDialog';
import { RuntimeEnvProvider, useRuntimeEnv } from '@/components/runtime-env';
import { SubscriptionLoadingOverlay } from '@/components/subscription-loading-overlay';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { buildBillingDetails, buildInvoiceInformationFormData } from '@/lib/billing-details-helper';
import { parseBool } from '@/lib/bool-parser';
import { SelectOptionVM } from '@/lib/models/view-models/view-models';
import { WithQueryClient } from '@/lib/query-client';
import { useServerActionQuery } from '@/lib/react-query';
import { isPaymentAlreadySucceeded } from '@/lib/stripe-payment-utils';
import { cn } from '@/lib/utils';
import { Banner, BannerDescription, BannerTitle } from '@/ui-lib/banner';
import { Button } from '@/ui-lib/button';
import { CardContainer } from '@/ui-lib/card';
import { Loader } from '@/ui-lib/loader';

import {
    cancelSubscription,
    createSubscriptionsWithPayment,
    fetchPaymentMethods,
    fetchReportGroups,
    fetchSubscription,
    reactivateSubscription,
    signupForCheckout,
    updateSubscription,
} from '../../../_components/api';
import { CartHeading, CartLayout } from '../../../_components/CartLayout';
import {
    CheckoutEmailForm,
    EmailFormData,
    SelectedReportsFormData,
    useCheckoutEmailForm,
    useSelectedReportsForm,
} from '../../../_components/CheckoutForm';
import { ReportGroupsResponse, SelectedPrice } from '../../../_components/types';
import { CancelSubscriptionDialog } from './CancelSubscriptionDialog';
import { useCheckoutAnalytics } from './checkoutAnalytics';
import { PaymentForm } from './PaymentForm';
import { ReportsList } from './ReportsList';
import { getFilteredItemsForCheckout } from './totals-calc';
import { PreviewRow, TotalsRow } from './TotalsRow';

interface CartFormContentProps {
    data: ReportGroupsResponse;
    subscription: SubscriptionSubscriptionData | undefined;
    subscriptionId: number | null;
    isManagingSubscription: boolean;

    initialData: {
        selectedPrices: Record<string, SelectedPrice>;
    };
    initialSubscriptionItemIds: string[];
    countries: SelectOptionVM[];
}

// LocalStorage key and expiry for pending checkout items (5 minutes)
const PENDING_CHECKOUT_KEY = 'pendingCheckoutItems';
const PENDING_CHECKOUT_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

function CartFormContent({
    initialData,
    data,
    subscription,
    subscriptionId,
    isManagingSubscription,
    initialSubscriptionItemIds,
    countries,
}: CartFormContentProps) {
    const router = useRouter();
    const queryClient = useQueryClient();
    const { status, data: session } = useSession();
    const { STRIPE_PUBLISHABLE_KEY: stripePublishableKey } = useRuntimeEnv();
    const [email, setEmail] = useState('');
    const [showLoginForm, setShowLoginForm] = useState(false);
    const [showLoginFormDefaultEmail, setShowLoginFormDefaultEmail] = useState('');
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [showAccountExistsMessage, setShowAccountExistsMessage] = useState(false);
    const [showCancelDialog, setShowCancelDialog] = useState(false);

    // Payment method state
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
    const [isAddPaymentDialogOpen, setIsAddPaymentDialogOpen] = useState(false);
    const [hasPaymentMethodError, setHasPaymentMethodError] = useState(false);

    // Fetch invoice information for pre-filling payment form (only when authenticated)
    const { data: invoiceInformation, refetch: refetchInvoiceInformation } = useServerActionQuery({
        queryKey: ['invoice-information'],
        queryFn: getCurrentUserInvoiceInformation,
        enabled: status === 'authenticated',
    });

    const totalsRowRef = useRef<{
        refetchTotal: () => void;
    }>(null);

    // Pending checkout data - stored when user creates account and needs to add payment method
    const [pendingCheckoutItems, setPendingCheckoutItems] = useState<Array<{
        priceId: number;
        priceType: 'group';
    }> | null>(null);
    const [isCreatingAccount, setIsCreatingAccount] = useState(false);

    // Forms using React Hook Form
    const emailForm = useCheckoutEmailForm();
    const selectedReportsForm = useSelectedReportsForm(initialData);

    // Restore pending checkout items from localStorage on mount (survives session changes)
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const stored = localStorage.getItem(PENDING_CHECKOUT_KEY);
            if (stored) {
                try {
                    const { items, timestamp } = JSON.parse(stored);

                    // Check if expired (5 minutes)
                    if (Date.now() - timestamp > PENDING_CHECKOUT_EXPIRY_MS) {
                        localStorage.removeItem(PENDING_CHECKOUT_KEY);
                        return;
                    }

                    // Remove from localStorage after reading (one-time use)
                    localStorage.removeItem(PENDING_CHECKOUT_KEY);

                    setPendingCheckoutItems(items);

                    // Also restore the visual form selection
                    const restoredPrices: Record<string, SelectedPrice> = {};
                    for (const item of items) {
                        if (item.type === 'group') {
                            const report = data.reportGroups.find((r) => r.price?.id === item.priceId);
                            if (report) {
                                restoredPrices[`group-${report.id}`] = { priceId: item.priceId, type: 'group' };
                            }
                        }
                    }
                    if (Object.keys(restoredPrices).length > 0) {
                        selectedReportsForm.reset({ selectedPrices: restoredPrices });
                    }
                } catch (e) {
                    console.error('Failed to restore pending checkout items', e);
                    // Ignore parse errors
                    localStorage.removeItem(PENDING_CHECKOUT_KEY);
                }
            }
        }
        // Only run on mount and when session status changes
    }, [status]);

    // Mutations
    const subscriptionMutation = useMutation({
        mutationFn: createSubscriptionsWithPayment,
        onSuccess: async (data, variables) => {
            if (data.requiresAction && data.clientSecret) {
                // Payment confirmation required (always the case with default_incomplete).
                // For non-3DS cards this resolves immediately; for 3DS cards a modal appears.
                const stripe = await loadStripe(stripePublishableKey);
                if (!stripe) {
                    toast.error('Failed to load payment processor. Please try again.');
                    return;
                }
                const { error } = await stripe.confirmPayment({
                    clientSecret: data.clientSecret,
                    confirmParams: { payment_method: variables.paymentMethodId },
                    redirect: 'if_required',
                });
                if (error) {
                    // Check if the error is because payment already succeeded
                    if (isPaymentAlreadySucceeded(error)) {
                        // Payment succeeded - proceed to success page
                        // (logging is handled in the utility function)
                    } else {
                        // Real error - show message and stop
                        console.error('Payment confirmation error:', error);
                        toast.error(error.message || 'Payment failed. Please try again.');
                        return;
                    }
                }
            }
            toast.success('Subscription created successfully!');
            router.push(`/market-reports/checkout/success?subscription_id=${data.subscription.id}`);
        },
        onError: (error: Error) => {
            // Overlay will hide automatically when mutation error occurs (isPending becomes false)
            const errorMessage = error.message || 'Failed to create subscription';

            if (errorMessage.includes('account with this email already exists')) {
                if (email) {
                    setShowLoginFormDefaultEmail(email);
                }
                setShowLoginForm(true);
                setShowAccountExistsMessage(true);
            } else {
                toast.error(errorMessage);
            }
        },
    });

    const cancelMutation = useMutation({
        mutationFn: () => cancelSubscription(subscriptionId!, false),
        onSettled: () => setShowCancelDialog(false),
        onSuccess: () => {
            const endDate = new Date(subscription!.currentPeriodEnd).toLocaleDateString();
            toast.success('Email Report subscription cancelled', {
                description: `Your email reports will remain active until ${endDate}, then will be automatically disabled.`,
            });
            queryClient.invalidateQueries({ queryKey: ['subscription', subscriptionId] });
        },
        onError: (error: Error) => toast.error(error.message || 'Failed to cancel subscription'),
    });

    const updateMutation = useMutation({
        mutationFn: ({ items }: { items: Array<{ priceId: number; priceType: 'group' }> }) =>
            updateSubscription(subscriptionId!, items),
        onSuccess: () => {
            toast.success('Subscription updated successfully');
            queryClient.invalidateQueries({ queryKey: ['subscription', subscriptionId] });
        },
        onError: (error: Error) => {
            toast.error(error.message || 'Failed to update subscription');
        },
    });

    const reactivateMutation = useMutation({
        mutationFn: () => reactivateSubscription(subscriptionId!),
        onSuccess: () => {
            toast.success('Subscription reactivated successfully');
            queryClient.invalidateQueries({ queryKey: ['subscription', subscriptionId] });
        },
        onError: (error: Error) => {
            toast.error(error.message || 'Failed to reactivate subscription');
        },
    });

    // Check if subscription is pending cancellation
    const isPendingCancellation = subscription?.cancelAtPeriodEnd === true;

    const currentSelected = useWatch({
        control: selectedReportsForm.control,
        name: 'selectedPrices',
    });

    const saveCurrentCartToStorage = async () => {
        // save to localStorage to survive session change re-renders
        if (typeof window !== 'undefined') {
            localStorage.setItem(
                PENDING_CHECKOUT_KEY,
                JSON.stringify({ items: Array.from(Object.values(currentSelected)), timestamp: Date.now() })
            );
        }
    };

    const handleSuccessfullyLoggedin = async () => {
        setIsLoggingIn(true);

        try {
            await saveCurrentCartToStorage();
            await getSession();
            toast.success('Logged in successfully');
            setIsLoggingIn(false);

            // refetch totals so tax is updated
            totalsRowRef.current?.refetchTotal();
        } catch (error) {
            console.error('error: ', error);
            setIsLoggingIn(false);
        }
    };

    const handleEmailFormSubmit = async (data: EmailFormData) => {
        setIsCreatingAccount(false);
        try {
            await saveCurrentCartToStorage();

            await signupForCheckout(data.email);

            // Refresh the session to get the new authenticated state
            await getSession();

            // Invalidate payment methods query to fetch fresh for the new user
            queryClient.invalidateQueries({ queryKey: ['payment-methods'] });

            toast.success('Please enter your details to continue with your subscription.');

            // refetch totals so tax is updated
            totalsRowRef.current?.refetchTotal();
        } catch (error: any) {
            const errorMessage = error.message || 'Failed to create account';
            if (errorMessage.includes('already exists')) {
                setShowLoginFormDefaultEmail(data.email);
                setShowLoginForm(true);
                setShowAccountExistsMessage(true);
            } else {
                toast.error(errorMessage);
                emailForm.setError('root', {
                    type: 'server',
                    message: errorMessage,
                });
            }
        } finally {
            setIsCreatingAccount(false);
        }
    };

    const handleCheckout = async (formData: SelectedReportsFormData) => {
        setHasPaymentMethodError(false);

        // Use getFilteredItemsForCheckout to filter out Items that are included in subscription (no price needed)
        const selectedItems = getFilteredItemsForCheckout(formData.selectedPrices, reports);
        if (selectedItems.length === 0) {
            toast.error('Please select at least one report');
            return;
        }

        // Handle subscription updates
        if (isManagingSubscription && subscriptionId) {
            updateMutation.mutate({
                items: selectedItems,
            });
            return;
        }

        if (status === 'unauthenticated') {
            toast.error('Must be logged in to checkout. Please create an account or log in to continue.');
            return;
        } else {
            if (!selectedPaymentMethodId) {
                toast.error('Please select a payment method');

                setHasPaymentMethodError(true);

                return;
            }

            subscriptionMutation.mutate({
                items: selectedItems,
                paymentMethodId: selectedPaymentMethodId,
            });
        }
    };

    const handlePaymentSubmit = async (formData: SelectedReportsFormData) => {
        if (isPendingCancellation) {
            reactivateMutation.mutate();
            return;
        }

        const allSelectedItems = Array.from(Object.entries(formData.selectedPrices));
        if (allSelectedItems.length === 0) {
            setShowCancelDialog(true);
            return;
        }

        await handleCheckout(formData);
    };

    const handlePaymentMethodSelect = (methodId: string | null) => {
        setSelectedPaymentMethodId(methodId);
        if (methodId === null) {
            setIsAddPaymentDialogOpen(true);
        }
        setHasPaymentMethodError(false);
    };

    const handlePaymentMethodAdded = async (newPaymentMethodId?: string) => {
        // Invalidate to refresh payment methods list
        await queryClient.invalidateQueries({ queryKey: ['payment-methods'] });

        // If we have pending checkout items, proceed with subscription creation
        if (pendingCheckoutItems && pendingCheckoutItems.length > 0) {
            setHasPaymentMethodError(false);

            // Use the new payment method ID if provided, otherwise wait for query to resolve
            if (newPaymentMethodId) {
                setSelectedPaymentMethodId(newPaymentMethodId);
            } else {
                // If no ID provided, we need to fetch the payment methods
                // and use the default or first one
                const result = await queryClient.fetchQuery({
                    queryKey: ['payment-methods'],
                    queryFn: fetchPaymentMethods,
                });

                const methods = result?.paymentMethods || [];
                if (methods.length > 0) {
                    const defaultMethod = methods.find((pm) => pm.isDefault);
                    const methodToUse = defaultMethod?.id || methods[0].id;

                    setSelectedPaymentMethodId(methodToUse);
                }
            }
        } else {
            // No pending checkout, just reset to allow auto-select
            setSelectedPaymentMethodId(null);
        }
    };

    const handleInvoiceInformationAdded = () => {
        refetchInvoiceInformation();
        // refetch totals so tax is updated
        totalsRowRef.current?.refetchTotal();
    };

    const reports = data.reportGroups;

    // Check if all reports are included in the user's subscription
    const allReportsIncluded = useMemo(() => {
        if (status !== 'authenticated' || reports.length === 0) return false;
        return reports.every((report) => report.includedInSubscription === true);
    }, [status, reports]);

    // Get user's display name from session
    const userFullName = session?.user?.name || 'User';
    const sessionEmail = session?.user?.email || '';

    // Find the platform subscription for the current user
    const subscriptionDisplayName = data?.platformSubscription?.displayName || '';

    const isAuthenticated = status === 'authenticated';
    // Use watch() instead of useWatch() here. useWatch's parent-level subscription
    // can go stale after selectedReportsForm.reset() in the auth-restoration effect,
    // leaving the PaymentForm rendering with selectedPricesCount=0 even though the
    // form (and child Controllers / TotalsRow) have entries. watch() reads via the
    // form's central subscription and stays in sync.
    const selectedPrices = selectedReportsForm.watch('selectedPrices') ?? {};

    const getRightColumnForm = () => {
        if (!isAuthenticated) {
            return showLoginForm ? (
                <EmailExistsForm
                    defaultEmail={showLoginFormDefaultEmail}
                    onToggleLoginForm={(show) => {
                        setShowLoginForm(show);
                        if (!show) {
                            setShowAccountExistsMessage(false);
                        }
                    }}
                    onSuccessfullyLoggedin={handleSuccessfullyLoggedin}
                />
            ) : (
                <UnauthenticatedCheckoutForm
                    emailForm={emailForm}
                    onSubmitEmailForm={handleEmailFormSubmit}
                    isLoggingIn={isLoggingIn}
                />
            );
        }

        /*
         * The country is the only information entered by the user that is
         * present both in the Personal and Company invoice forms - if it is
         * set we have enough to summarize their billing details and skip the
         * dedicated invoice step. PaymentForm renders the summary inline with
         * an Edit button so users can still update those details on demand.
         */
        const hasInvoiceInformation = !!invoiceInformation?.address?.country;
        if (hasInvoiceInformation) {
            /*
             * We can't use the dirty form state to detect if any change to the
             * subscription has been made since reports can also be selected by
             * means of the "add" query parameter.
             */
            const selectedSubscriptions = new Set(Object.keys(selectedReportsForm.getValues().selectedPrices));
            const changedSubscriptions = selectedSubscriptions.symmetricDifference(new Set(initialSubscriptionItemIds));
            return (
                <PaymentForm
                    areSubscriptionItemsChanged={changedSubscriptions.size > 0}
                    countries={countries}
                    hasPaymentMethodError={hasPaymentMethodError}
                    invoiceInformation={invoiceInformation ?? null}
                    isCancellationPending={isPendingCancellation}
                    isNewSubscription={!isManagingSubscription}
                    isProcessing={
                        subscriptionMutation.isPending ||
                        isLoggingIn ||
                        updateMutation.isPending ||
                        isCreatingAccount ||
                        reactivateMutation.isPending
                    }
                    onInvoiceInformationUpdated={handleInvoiceInformationAdded}
                    onSelectPaymentMethod={handlePaymentMethodSelect}
                    onSubmit={selectedReportsForm.handleSubmit(handlePaymentSubmit)}
                    selectedPaymentMethodId={selectedPaymentMethodId}
                    selectedPricesCount={Object.keys(selectedPrices).length}
                    userEmail={session?.user?.email ?? email ?? sessionEmail ?? ''}
                    userName={invoiceInformation?.name ?? email ?? session?.user?.name ?? sessionEmail}
                />
            );
        }

        const teamId = invoiceInformation?.teamId;
        const initialValues = invoiceInformation ? buildInvoiceInformationFormData(invoiceInformation) : undefined;
        return (
            <>
                <div className="space-y-3 pt-4 border-neutral-700">
                    <h2 className="text-xl font-semibold text-neutral-100">Billing details</h2>
                    <p className="text-sm text-neutral-300 pt-2">
                        Please confirm your billing details before continuing to payment.
                    </p>
                </div>
                <div className="space-y-3 py-4 border-t border-neutral-700">
                    {teamId != null ? (
                        <InvoiceInformationForm
                            isCheckoutForm
                            teamId={teamId}
                            countries={countries}
                            initialValues={initialValues}
                            invoiceInformationAdded={handleInvoiceInformationAdded}
                        />
                    ) : (
                        <Loader mode="light" size="sm" />
                    )}
                </div>
            </>
        );
    };

    return (
        <CartLayout
            banner={
                isManagingSubscription && subscription ? (
                    isPendingCancellation ? (
                        <Banner variant="warning">
                            <BannerTitle>Subscription Scheduled for Cancellation</BannerTitle>
                            <BannerDescription>
                                Your subscription to <strong>{subscription.displayName}</strong> is scheduled to be
                                canceled at the end of your billing period. To make changes to your subscription, you
                                must first reactivate it using the button below.
                            </BannerDescription>
                        </Banner>
                    ) : (
                        <Banner variant="info">
                            <BannerTitle>Managing Subscription</BannerTitle>
                            <BannerDescription>
                                You are currently managing your subscription to{' '}
                                <strong>{subscription.displayName}</strong>. Select a different plan to switch your
                                subscription, or cancel it below.
                            </BannerDescription>
                        </Banner>
                    )
                ) : undefined
            }
            leftColumn={
                <CardContainer className={`p-6 rounded-2xl relative ${isPendingCancellation ? 'opacity-50' : ''}`}>
                    {/* Overlay to block interactions when subscription is pending cancellation */}
                    {isPendingCancellation && (
                        <div className="absolute inset-0 bg-transparent z-10 cursor-not-allowed" />
                    )}
                    <div className="space-y-4">
                        {isManagingSubscription && (
                            <div className="pb-2 border-b border-neutral-700">
                                <div className="flex items-start gap-2">
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-neutral-100">
                                            {isPendingCancellation
                                                ? 'Subscription Modifications Disabled'
                                                : 'Choose Your Subscription'}
                                        </h3>
                                        <p className="text-sm text-neutral-400 mt-1">
                                            {isPendingCancellation
                                                ? 'Reactivate your subscription to make changes'
                                                : 'Select a different plan to switch, or deselect and choose another option'}
                                        </p>
                                    </div>
                                    {!isPendingCancellation && (
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <button
                                                        type="button"
                                                        className="mt-1 text-neutral-500 hover:text-neutral-300 transition-colors"
                                                    >
                                                        <IconInfoCircle className="w-5 h-5" />
                                                    </button>
                                                </TooltipTrigger>
                                                <TooltipContent side="left" className="max-w-xs">
                                                    <p className="text-sm">
                                                        You can deselect your current subscription and choose a
                                                        different one. You must have at least one subscription selected
                                                        to proceed, or cancel your subscription if you no longer want
                                                        it.
                                                    </p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    )}
                                </div>
                            </div>
                        )}

                        <ReportsList
                            reports={reports}
                            currentSubscriptionItemIds={initialSubscriptionItemIds}
                            control={selectedReportsForm.control}
                            allIncluded={allReportsIncluded}
                        />

                        {!allReportsIncluded ? (
                            <>
                                <TotalsRow
                                    isManagingSubscription={isManagingSubscription}
                                    control={selectedReportsForm.control}
                                    reports={reports}
                                    initialSelectedPrices={initialData.selectedPrices}
                                    queryRef={totalsRowRef}
                                />

                                {isManagingSubscription && subscriptionId && (
                                    <PreviewRow
                                        subscriptionId={subscriptionId}
                                        control={selectedReportsForm.control}
                                        reports={reports}
                                    />
                                )}
                            </>
                        ) : null}
                    </div>
                </CardContainer>
            }
            rightColumn={
                allReportsIncluded ? (
                    <CardContainer className="p-6 rounded-2xl">
                        <div className="space-y-6">
                            <div className="space-y-3">
                                <h2 className="text-xl font-semibold text-neutral-100">{userFullName},</h2>
                                <p className="text-neutral-300">
                                    You have an active{' '}
                                    <span className="font-semibold text-neutral-100">{subscriptionDisplayName}</span>{' '}
                                    package, and all Market Reports are included.
                                </p>
                            </div>
                            <Link href="/platform/market-reports/">
                                <Button variant="cta" size="lg" className="w-full mt-6" type="button">
                                    View all Reports
                                </Button>
                            </Link>
                        </div>
                    </CardContainer>
                ) : (
                    <>
                        <CardContainer className="p-6 rounded-2xl">
                            <div className="space-y-6">{getRightColumnForm()}</div>
                        </CardContainer>
                    </>
                )
            }
            overlay={<SubscriptionLoadingOverlay isOpen={subscriptionMutation.isPending} />}
            dialogs={
                <>
                    {/* Dialogs rendered outside form to prevent nested form issues */}
                    <CancelSubscriptionDialog
                        open={showCancelDialog}
                        subscription={subscription}
                        isLoading={cancelMutation.isPending}
                        onConfirm={() => cancelMutation.mutate()}
                        onCancel={() => setShowCancelDialog(false)}
                    />

                    <AddPaymentMethodDialog
                        open={isAddPaymentDialogOpen}
                        onOpenChange={setIsAddPaymentDialogOpen}
                        onSuccess={handlePaymentMethodAdded}
                        defaultBillingDetails={buildBillingDetails({
                            invoiceInformation,
                            sessionUser: session?.user,
                            fallbackEmail: email,
                        })}
                    />
                </>
            }
        />
    );
}

const asArray = (value: string | string[] | null): string[] => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
};

const UnauthenticatedCheckoutForm = ({
    emailForm,
    onSubmitEmailForm,
    isLoggingIn,
}: {
    emailForm: ReturnType<typeof useCheckoutEmailForm>;
    onSubmitEmailForm: (data: EmailFormData) => void;
    isLoggingIn: boolean;
}) => {
    const analytics = useCheckoutAnalytics({
        isManagingSubscription: false,
        isAuthenticated: false,
        showLoginForm: true,
        isEditingInvoiceInformation: false,
        selectedPricesCount: 0,
        selectedPaymentMethodId: null,
        paymentMethodsCount: 0,
        isLoadingPaymentMethods: false,
        hasPaymentMethodError: false,
        isProcessing: false,
        isLoggingIn,
        isFormDirty: false,
        isPendingCancellation: false,
    });

    return (
        <div className="space-y-3 pt-4">
            <form
                onSubmit={emailForm.handleSubmit((data) => {
                    analytics.trackEmailSubmitted();
                    onSubmitEmailForm(data);
                })}
            >
                <CartHeading>Customer information</CartHeading>
                <CheckoutEmailForm
                    form={emailForm}
                    disabled={isLoggingIn}
                    onEmailChange={() => analytics.trackEmailStarted()}
                />
                <Button
                    variant="primary"
                    size="lg"
                    className={cn('w-full mt-20', {
                        'opacity-50 cursor-not-allowed': isLoggingIn,
                    })}
                    disabled={isLoggingIn}
                    type="submit"
                    isLoading={isLoggingIn}
                    data-testid="authenticate-button"
                    onClick={() => analytics.trackCheckoutCtaClicked()}
                >
                    Continue
                </Button>
            </form>
        </div>
    );
};

const EmailExistsForm = ({
    defaultEmail,
    onToggleLoginForm,
    onSuccessfullyLoggedin,
}: {
    defaultEmail: string;
    onToggleLoginForm: (show: boolean) => void;
    onSuccessfullyLoggedin: () => void;
}) => (
    <div className="space-y-3 pt-4 border-neutral-700">
        <Banner variant="warning">
            <BannerTitle>Account Already Exists</BannerTitle>
            <BannerDescription>
                An account with this email already exists. Please sign in with your password to continue.
                <br />
                <br />
                Not your account?{' '}
                <a
                    href=""
                    className="text-darkBlue underline"
                    onClick={(e) => {
                        e.preventDefault();
                        onToggleLoginForm(false);
                    }}
                >
                    Use a different email
                </a>
            </BannerDescription>
        </Banner>

        <div
            className={cn(
                'mx-6 lg:mx-0 ' + 'col-start-8 col-span-4 ' + 'bg-secondary-100 rounded-2xl',
                `[@media(min-height:${600 + 300}px)]:min-h-[${600}px]`
            )}
        >
            <LoginForm
                onLoginComplete={(_) => onSuccessfullyLoggedin()}
                defaultEmail={defaultEmail}
                lockedEmail
                noSignupLink
            ></LoginForm>
        </div>
    </div>
);

function CartPageComponent({ countries }: { countries: SelectOptionVM[] }) {
    const router = useRouter();
    const searchParams = useSearchParams();
    const { status } = useSession();

    // Get subscriptionId from query params
    const subscriptionIdParam = searchParams.get('subscriptionId');
    const subscriptionId = subscriptionIdParam ? parseInt(subscriptionIdParam, 10) : null;
    const isManagingSubscription = subscriptionId !== null && !isNaN(subscriptionId);

    const urlAddItems = asArray(searchParams.getAll('add'));
    const selectAll = parseBool(searchParams.get('all')) ?? false;

    // Queries
    const { data, isLoading, isError, error } = useQuery<ReportGroupsResponse, Error>({
        queryKey: ['market-reports', 'groups', 'includeTrials'],
        queryFn: () => fetchReportGroups(true),
    });

    const {
        data: subscription,
        isLoading: isLoadingSubscription,
        isError: isSubscriptionError,
        error: subscriptionError,
    } = useQuery<SubscriptionSubscriptionData, Error>({
        queryKey: ['subscription', subscriptionId],
        queryFn: () => fetchSubscription(subscriptionId!),
        enabled: isManagingSubscription && status === 'authenticated',
    });

    // Memoize current subscription item IDs
    const initialSubscriptionItemIds = useMemo(() => {
        if (!subscription || !isManagingSubscription) return [];

        const ids: string[] = [];

        if (subscription.items && subscription.items.length > 0) {
            subscription.items.forEach((item) => {
                // Extract the actual ID from the item name or use a different approach
                if (item.type === 'group') {
                    // Find the group that has this priceId (skip groups without prices)
                    const report = data?.reportGroups.find((r) => r.price && r.price.id === item.priceId);
                    if (report) {
                        ids.push(`group-${report.id}`);
                    }
                }
            });
        }

        return ids;
    }, [subscription, isManagingSubscription, data?.reportGroups]);

    const initialData = useMemo(() => {
        const fromUrl = urlAddItems.length > 0;

        // When selectAll is true, pre-select all available reports with prices
        if (selectAll && data) {
            const initialPrices: Record<string, SelectedPrice> = {};

            data.reportGroups.forEach((report) => {
                // Only select reports that have a price and are not already included in subscription
                if (report.price && !report.includedInSubscription) {
                    initialPrices[`group-${report.id}`] = { priceId: report.price.id, type: 'group' };
                }
            });

            return {
                selectedPrices: initialPrices,
            };
        }

        if (!isManagingSubscription || !subscription || !data) {
            if (fromUrl) {
                const initialPrices: Record<string, SelectedPrice> = {};

                urlAddItems.forEach((item) => {
                    const report = data?.reportGroups.find((r) => `${r.id}` === item);
                    if (report && report.price) {
                        initialPrices[`group-${report.id}`] = { priceId: report.price.id, type: 'group' };
                    }
                });

                return {
                    selectedPrices: initialPrices,
                };
            }

            return {
                selectedPrices: {},
            };
        }

        const initialPrices: Record<string, SelectedPrice> = {};

        const allInitial = [...initialSubscriptionItemIds, ...urlAddItems.map((item) => `group-${item}`)];

        allInitial.forEach((prefixedId) => {
            const [type, id] = prefixedId.split('-');

            if (type === 'group') {
                const report = data.reportGroups.find((r) => `${r.id}` === id);
                if (report) {
                    const monthlyPrice = report.price;
                    if (monthlyPrice) {
                        initialPrices[prefixedId] = { priceId: monthlyPrice.id, type: 'group' };
                    }
                }
            }
        });

        return {
            selectedPrices: initialPrices,
        };
    }, [
        isManagingSubscription,
        subscription,
        data,
        data?.reportGroups,
        initialSubscriptionItemIds,
        urlAddItems,
        selectAll,
    ]);

    // Show loading state while fetching data
    if (isLoading || (isManagingSubscription && isLoadingSubscription)) {
        return (
            <div className="min-h-screen w-full bg-neutral-950 flex items-center justify-center">
                <Loader mode="light" size="md" />
            </div>
        );
    }

    // Show error for report groups
    if (isError) {
        return (
            <div className="min-h-screen w-full bg-neutral-950 p-8">
                <CardContainer>
                    <Banner variant="error">
                        <BannerTitle>Error loading market reports</BannerTitle>
                        <BannerDescription>{error?.toString()}</BannerDescription>
                    </Banner>
                </CardContainer>
            </div>
        );
    }

    // Show error for subscription
    if (isManagingSubscription && isSubscriptionError) {
        return (
            <div className="min-h-screen w-full bg-neutral-950 p-8">
                <CardContainer>
                    <Banner variant="error">
                        <BannerTitle>Error loading subscription</BannerTitle>
                        <BannerDescription>{subscriptionError?.toString()}</BannerDescription>
                    </Banner>
                    <div className="mt-4">
                        <Button type="button" onClick={() => router.push('/market-reports/cart')}>
                            Go to Cart
                        </Button>
                    </div>
                </CardContainer>
            </div>
        );
    }

    // Only render the form content when we have all necessary data
    return (
        <CartFormContent
            data={data!}
            subscription={subscription}
            subscriptionId={subscriptionId}
            isManagingSubscription={isManagingSubscription}
            initialData={initialData}
            initialSubscriptionItemIds={initialSubscriptionItemIds}
            countries={countries}
        />
    );
}

export const CartPage = ({ countries }: { countries: SelectOptionVM[] }) => {
    return (
        <WithQueryClient>
            <RuntimeEnvProvider>
                <CartPageComponent countries={countries} />
            </RuntimeEnvProvider>
        </WithQueryClient>
    );
};
